// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { Padding } from "@conklin/App/EditorTemplates/PaddingField";
import Button from "@conklin/MobiusOverrides/Button/Button";
import StyledBox, { StyledBoxStyles } from "@conklin/XDL/Components/Common/Box";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import React, { FC } from "react";

const enum fields {
    title = "title",
    titleSizeVariant = "titleSizeVariant",
    description = "description",
    linkText = "linkText",
    link = "link",
    linkStyle = "linkStyle",
    padding = "padding",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.titleSizeVariant]: "h1" | "h2" | "h3" | "h4" | "h5" | "h6";
        [fields.description]: string;
        [fields.linkText]: string;
        [fields.link]: LinkFieldValue;
        [fields.linkStyle]: "link" | "linkWithoutArrow" | "button";
        [fields.padding]: Padding;
    };
}

export interface HaveQuestionsStyles {
    wrapper?: StyledBoxStyles;
    title?: TypographyPresentationProps;
    description?: TypographyPresentationProps;
    link?: LinkPresentationProps;
    linkWithoutArrow?: LinkPresentationProps;
}

export const haveQuestionsStyles: HaveQuestionsStyles = {};

const styles = haveQuestionsStyles;

type Props = OwnProps & WidgetProps;

const HaveQuestions: FC<Props> = ({ fields }) => {
    const link = useGetLink(fields.link).url || "";
    return (
        <StyledBox extendedStyles={styles.wrapper} data-test-selector="have-question" {...{ padding: fields.padding }}>
            <Typography variant={fields.titleSizeVariant} forwardAs={fields.titleSizeVariant} {...styles.title}>
                {fields.title}
            </Typography>
            <Typography {...styles.description}>{fields.description}</Typography>
            {fields.link.value && fields.linkStyle === "link" && (
                <Link href={link} {...styles.link}>
                    {fields.linkText}
                </Link>
            )}
            {fields.link.value && fields.linkStyle === "linkWithoutArrow" && (
                <Link href={link} {...styles.linkWithoutArrow}>
                    {fields.linkText}
                </Link>
            )}
            {fields.link.value && fields.linkStyle === "button" && (
                <Button
                    variant="primary"
                    onClick={() => {
                        window.location.href = link;
                    }}
                    sizeVariant="medium"
                    aria-label={fields.linkText}
                >
                    {fields.linkText}
                </Button>
            )}
        </StyledBox>
    );
};

const widgetModule: WidgetModule = {
    component: HaveQuestions,
    definition: {
        group: "Common",
        displayName: "Have Questions",
        fieldDefinitions: [
            {
                fieldType: "Contextual",
                name: fields.title,
                editorTemplate: "TextField",
                displayName: "Title",
                defaultValue: "Have Questions On Your Purchase?",
                isRequired: true,
            },
            {
                fieldType: "Contextual",
                name: fields.titleSizeVariant,
                editorTemplate: "DropDownField",
                displayName: "Title Variant",
                options: [
                    { value: "h1", displayName: "h1" },
                    { value: "h2", displayName: "h2" },
                    { value: "h3", displayName: "h3" },
                    { value: "h4", displayName: "h4" },
                    { value: "h5", displayName: "h5" },
                    { value: "h6", displayName: "h6" },
                ],
                defaultValue: "h4",
                isRequired: true,
            },
            {
                fieldType: "Contextual",
                name: fields.description,
                editorTemplate: "MultilineTextField",
                displayName: "Description",
                defaultValue: "",
                isRequired: true,
            },
            {
                fieldType: "Contextual",
                name: fields.link,
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                isRequired: true,
            },
            {
                fieldType: "Contextual",
                name: fields.linkText,
                editorTemplate: "TextField",
                displayName: "Link Text",
                defaultValue: "Get Support",
                isRequired: true,
            },
            {
                fieldType: "Contextual",
                name: fields.linkStyle,
                editorTemplate: "DropDownField",
                displayName: "Link Style",
                options: [
                    { value: "link", displayName: "Link" },
                    { value: "linkWithoutArrow", displayName: "Link Without Arrow" },
                    { value: "button", displayName: "Button" },
                ],
                defaultValue: "link",
                isRequired: true,
            },
            {
                name: "padding",
                displayName: "Padding",
                editorTemplate: "PaddingField",
                defaultValue: {},
                fieldType: "General",
            },
        ] as any,
    },
};

export default widgetModule;
